/**
 * §7.4 站台作業。補秤區塊需 `receipt.supplement`。
 *
 * 這一頁做兩件事：
 *   1. 繳庫：把工單手上的在製批變成產出料件。可以**分選成多車**
 *      （§9 規則 10：多個繳庫行持有互斥 panelIds，**不是料件分裂**）
 *   2. 補秤：為既有的繳庫行補上重量（mockup §10 步 17）。
 *      這是「量測缺口只是設定」的示範——啟用量測點後補秤，結算就從
 *      「無法結算」變成正常
 */

import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client.js'
import { useMutation, useQuery } from '../api/hooks.js'
import { useAuth } from '../auth/AuthContext.js'
import { Alert, Button, Card, Empty, Field, Mono, Pill, Select, Table, Td, TextInput } from '../components/ui.js'
import {
  emptyWeighValue, toWeighPayload, WeighEntry, weighComplete, weighNetKg, useWeighDraft,
  type PanelMeta, type WeighValue,
} from '../components/WeighEntry.js'
import { useConfig, useDict } from '../config/ConfigContext.js'
import { count, kgUnit } from '../../../shared/lib/format.js'
import { Link, navigate } from '../router.js'

interface Lot {
  issue_line_id: string
  source_item_id: string
  material_code: string
  issued_kg: string | null
  weight_source: string
  split_basis: string | null
  resolution_grade: string
  homogeneity_grade: string
  panel_ids: string[]
}

interface Receipt {
  receipt_line_id: string
  new_item_id: string
  material_code: string
  net_kg: string | null
  container_id: string | null
  location_id: string | null
  panel_count: string
  per_panel_weighs: string
}

interface WorkOrderDetail {
  workOrder: { work_order_id: string; station_id: string; resource_id: string; status: string; planned_count: number }
  lots: Lot[]
  receipts: Receipt[]
  panels: PanelMeta[]
}

export function StationWork({ stationId }: { stationId: string }) {
  const dict = useDict()
  const list = useQuery<{ items: { work_order_id: string; station_id: string; resource_id: string }[] }>(
    `/api/work-orders?status=open&stationId=${encodeURIComponent(stationId)}&limit=50`,
  )
  const [woId, setWoId] = useState<string | null>(null)

  const items = list.data?.items ?? []
  useEffect(() => {
    if (!woId && items.length === 1) setWoId(items[0]!.work_order_id)
  }, [items, woId])

  return (
    <div className="space-y-4">
      <Card
        title={`${dict.station(stationId)} 站台作業`}
        subtitle="繳庫與補秤"
        right={<Button size="sm" onClick={list.reload}>重新整理</Button>}
      >
        {items.length === 0 && !list.loading && (
          <Empty>
            這一站目前沒有進行中的工單。
            <Link to="/issue" className="ml-1 underline">開工單並領料</Link>
          </Empty>
        )}
        {items.length > 0 && (
          <Field label="工單">
            <Select value={woId ?? ''} onChange={(e) => setWoId(e.target.value || null)}>
              <option value="">請選擇</option>
              {items.map((w) => (
                <option key={w.work_order_id} value={w.work_order_id}>
                  {w.work_order_id} · {dict.resource(w.resource_id)}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </Card>

      {woId && <WorkOrderPanel workOrderId={woId} onPosted={() => list.reload()} />}
    </div>
  )
}

function WorkOrderPanel({ workOrderId, onPosted }: { workOrderId: string; onPosted: () => void }) {
  const { config } = useConfig()
  const dict = useDict()
  const { can } = useAuth()
  const detail = useQuery<WorkOrderDetail>(`/api/work-orders/${encodeURIComponent(workOrderId)}`)

  const close = useMutation<void, unknown>(async (_a, key) =>
    api.post(`/api/work-orders/${encodeURIComponent(workOrderId)}/close`, {}, { idempotencyKey: key }))

  if (detail.loading) return <p className="text-sm text-ink-500">載入中…</p>
  if (detail.error) return <Alert tone="danger" title="讀不到工單">{detail.error.message}</Alert>
  if (!detail.data) return null

  const { workOrder, lots, receipts, panels } = detail.data
  const station = workOrder.station_id
  const missingWeight = receipts.filter((r) => r.net_kg === null && Number(r.per_panel_weighs) === 0)

  return (
    <>
      <Card title="這張工單手上的料（在製批）" subtitle="領出後尚未繳庫">
        {lots.length === 0 ? <Empty>還沒領料。</Empty> : (
          <Table head={['在製批', '來源料件', '物料', '片數', '領出重量', '解析度']} dense>
            {lots.map((l) => (
              <tr key={l.issue_line_id}>
                <Td><Mono>{l.issue_line_id}</Mono></Td>
                <Td><Mono>{l.source_item_id}</Mono></Td>
                <Td>{dict.material(l.material_code)}</Td>
                <Td className="tabular-nums">{count(l.panel_ids.length)}</Td>
                <Td className="tabular-nums">{l.issued_kg === null ? '—' : kgUnit(Number(l.issued_kg), 1)}</Td>
                <Td><Pill tone={l.resolution_grade === 'R3' ? 'amber' : 'ink'}>{l.resolution_grade}</Pill></Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {lots.length > 0 && config && (
        <ReceiptForm
          workOrderId={workOrderId}
          station={station}
          lots={lots}
          panels={panels}
          config={config}
          onPosted={() => { detail.reload(); onPosted() }}
        />
      )}

      {receipts.length > 0 && (
        <Card title="已繳庫" subtitle={`${count(receipts.length)} 筆`}>
          <Table head={['繳庫行', '產出料件', '物料', '片數', '重量', '容器', '儲位']} dense>
            {receipts.map((r) => (
              <tr key={r.receipt_line_id} className={r.net_kg === null && Number(r.per_panel_weighs) === 0 ? 'bg-amber-50' : ''}>
                <Td><Mono>{r.receipt_line_id}</Mono></Td>
                <Td><Mono>{r.new_item_id}</Mono></Td>
                <Td>{dict.material(r.material_code)}</Td>
                <Td className="tabular-nums">{r.panel_count}</Td>
                <Td className="tabular-nums">
                  {r.net_kg !== null
                    ? kgUnit(Number(r.net_kg), 1)
                    : Number(r.per_panel_weighs) > 0
                      ? `逐片 ${r.per_panel_weighs} 筆`
                      : <Pill tone="amber">無重量</Pill>}
                </Td>
                <Td className="text-xs">{dict.container(r.container_id)}</Td>
                <Td className="text-xs">{dict.location(r.location_id)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      {/* mockup §10 步 17：補秤。§6.1 需要 receipt.supplement */}
      {missingWeight.length > 0 && config && (
        can('receipt.supplement') ? (
          <SupplementForm
            workOrderId={workOrderId}
            station={station}
            lines={missingWeight}
            config={config}
            onPosted={() => { detail.reload(); onPosted() }}
          />
        ) : (
          <Card title="有繳庫行沒有重量" tone="warn">
            <Alert tone="warn" title="需要補秤，但您沒有補秤權限">
              {missingWeight.length} 筆繳庫行還沒有重量，這張工單目前算不出損耗。
              補秤需要 <Mono>receipt.supplement</Mono> 權限，請找主管處理。
            </Alert>
          </Card>
        )
      )}

      <Card title="結算與關單">
        <div className="flex flex-wrap gap-2">
          <Link to={`/balance/${workOrderId}`}>
            <Button size="lg">查看站台結算</Button>
          </Link>
          {workOrder.status === 'open' && (
            <Button
              size="lg" variant="primary" disabled={close.pending}
              onClick={async () => {
                if (!confirm(`確定要關閉工單 ${workOrderId} 嗎？關閉後不能再領料或繳庫。`)) return
                const r = await close.run()
                if (r !== null) { detail.reload(); onPosted() }
              }}
            >{close.pending ? '關閉中…' : '關閉工單'}</Button>
          )}
        </div>
        {close.error && <div className="mt-3"><Alert tone="danger">{close.error.message}</Alert></div>}
        {missingWeight.length > 0 && (
          <p className="mt-3 text-xs text-amber-700">
            還有 {missingWeight.length} 筆繳庫行沒有重量。關單不會被擋，但結算會顯示「無法結算」
            並說明原因、影響與解法——系統不會替您把數字湊出來。
          </p>
        )}
      </Card>
    </>
  )
}

// ── 繳庫 ───────────────────────────────────────────────────────────────────

interface LineDraft {
  key: number
  materialCode: string
  newItemId: string
  containerId: string
  locationId: string
  panelIds: string[]
  weigh: WeighValue
}

function ReceiptForm({ workOrderId, station, lots, panels, config, onPosted }: {
  workOrderId: string
  station: string
  lots: Lot[]
  panels: PanelMeta[]
  config: NonNullable<ReturnType<typeof useConfig>['config']>
  onPosted: () => void
}) {
  const dict = useDict()
  const itemPool = useQuery<{ ids: { id: string; status: string }[] }>('/api/id-pool?idType=item&limit=200')
  const [lines, setLines] = useState<LineDraft[]>([])
  const [seq, setSeq] = useState(1)

  // 本站應產出的物料（STATION_FLOW 在 shared/types/config.ts）
  const outputs = useMemo(() => {
    const inMat = lots[0]?.material_code
    return config.measurementPoints
      .filter((m) => m.stationId === station && m.kind === 'receipt' && m.materialCode !== inMat)
      .map((m) => m.materialCode as string)
  }, [config, station, lots])

  const allPanels = useMemo(() => lots.flatMap((l) => l.panel_ids), [lots])
  const metaMap = useMemo(() => new Map(panels.map((p) => [p.panel_id, p])), [panels])
  const availableIds = (itemPool.data?.ids ?? []).map((x) => x.id)
  const usedIds = new Set(lines.map((l) => l.newItemId))

  const addLine = (materialCode: string) => {
    const id = availableIds.find((x) => !usedIds.has(x)) ?? ''
    setLines((ls) => [...ls, {
      key: seq,
      materialCode,
      newItemId: id,
      containerId: '',
      locationId: '',
      // 預設整批帶入——分選成多車時再由現場調整
      panelIds: [...allPanels],
      weigh: emptyWeighValue(),
    }])
    setSeq((s) => s + 1)
  }

  const post = useMutation<void, { receiptLineIds: string[] }>(async (_a, key) =>
    api.post('/api/receipts', {
      workOrderId,
      lines: lines.map((l) => {
        const mp = config.measurementPoints.find(
          (m) => m.stationId === station && m.kind === 'receipt' && m.materialCode === l.materialCode,
        )
        return {
          newItemId: l.newItemId,
          materialCode: l.materialCode,
          containerId: l.containerId || null,
          locationId: l.locationId || null,
          panelIds: l.panelIds,
          // 量測點停用時不帶——沒有重量，且不硬湊（§9 規則 11）
          ...(mp?.enabled ? {
            measurementPointId: mp.measurementPointId,
            weigh: toWeighPayload(mp, l.weigh, l.panelIds),
          } : {}),
        }
      }),
    }, { idempotencyKey: key }))

  const ready = lines.length > 0 && lines.every((l) => {
    if (!l.newItemId) return false
    const mp = config.measurementPoints.find(
      (m) => m.stationId === station && m.kind === 'receipt' && m.materialCode === l.materialCode,
    )
    if (!mp || !mp.enabled) return true
    return weighComplete(mp, l.weigh, l.panelIds)
  })

  return (
    <Card
      title="繳庫"
      subtitle="§9 規則 10：分選成多車是多個繳庫行持有互斥 PV 號，不是料件分裂"
    >
      <div className="mb-3 flex flex-wrap gap-2">
        {outputs.map((code) => (
          <Button key={code} size="sm" onClick={() => addLine(code)}>
            + {dict.material(code)}
          </Button>
        ))}
        {outputs.length === 0 && (
          <p className="text-sm text-ink-500">
            本站沒有設定產出的量測點。請主管到作業設定確認。
          </p>
        )}
      </div>

      {lines.length === 0 ? (
        <Empty>按上面的按鈕新增繳庫行。同一種物料可以加多筆（分選成多車）。</Empty>
      ) : (
        <div className="space-y-4">
          {lines.map((l, i) => {
            const mp = config.measurementPoints.find(
              (m) => m.stationId === station && m.kind === 'receipt' && m.materialCode === l.materialCode,
            )
            const linePanels = l.panelIds
              .map((p) => metaMap.get(p))
              .filter((x): x is PanelMeta => Boolean(x))
            return (
              <div key={l.key} className="rounded-md border border-ink-200 p-3">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <strong className="text-sm">{dict.material(l.materialCode)}</strong>
                  <span className="text-xs text-ink-500">
                    {count(l.panelIds.length)} 片
                  </span>
                  <Button size="sm" variant="ghost" className="ml-auto"
                    onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>移除</Button>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="產出料件號" required>
                    <Select value={l.newItemId}
                      onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, newItemId: e.target.value } : x))}>
                      <option value="">請選擇</option>
                      {availableIds
                        .filter((id) => id === l.newItemId || !usedIds.has(id))
                        .slice(0, 60)
                        .map((id) => <option key={id} value={id}>{id}</option>)}
                    </Select>
                  </Field>
                  <Field label="容器">
                    <Select value={l.containerId}
                      onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, containerId: e.target.value } : x))}>
                      <option value="">請選擇</option>
                      {config.containers
                        .filter((c) => !c.forMaterials || c.forMaterials.includes(l.materialCode as never))
                        .map((c) => <option key={c.containerId} value={c.containerId}>{c.label}</option>)}
                    </Select>
                  </Field>
                  <Field label="儲位">
                    <Select value={l.locationId}
                      onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, locationId: e.target.value } : x))}>
                      <option value="">請選擇</option>
                      {config.locations.map((x) => <option key={x.locationId} value={x.locationId}>{x.label}</option>)}
                    </Select>
                  </Field>
                </div>

                <div className="mt-3">
                  <Field label="這一車包含哪些片"
                    hint="預設是整批。分選成多車時，把不屬於這一車的 PV 號移掉——各車的 PV 號必須互斥">
                    <PanelSubset
                      all={allPanels}
                      selected={l.panelIds}
                      excludeUsedBy={lines.filter((_, j) => j !== i).flatMap((x) => x.panelIds)}
                      onChange={(ids) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, panelIds: ids } : x))}
                    />
                  </Field>
                </div>

                {mp && (
                  <div className="mt-3">
                    <WeighEntry
                      mp={mp} config={config} materialCode={l.materialCode}
                      panels={linePanels}
                      value={l.weigh}
                      onChange={(v) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, weigh: v } : x))}
                    />
                    {mp.enabled && mp.granularity !== 'panel' && (
                      <p className="mt-1 text-xs text-ink-500">
                        淨重 {kgUnit(weighNetKg(mp, l.weigh, l.panelIds), mp.decimals)}
                      </p>
                    )}
                  </div>
                )}
                {!mp && (
                  <div className="mt-3">
                    <Alert tone="warn">
                      本站沒有針對 {dict.material(l.materialCode)} 的繳庫量測點，這一筆不會有重量。
                    </Alert>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {post.error && <div className="mt-3"><Alert tone="danger" title="繳庫失敗">{post.error.message}</Alert></div>}

      {lines.length > 0 && (
        <div className="mt-4">
          <Button variant="primary" size="lg" disabled={!ready || post.pending}
            onClick={async () => {
              const r = await post.run()
              if (r) { setLines([]); itemPool.reload(); onPosted() }
            }}>
            {post.pending ? '繳庫中…' : `繳庫 ${count(lines.length)} 筆`}
          </Button>
          {!ready && <p className="mt-2 text-xs text-amber-700">每一筆都要選料件號，實秤的量測點還要輸入完重量。</p>}
        </div>
      )}
    </Card>
  )
}

/** 分選成多車時挑這一車的片。各車互斥由畫面幫忙擋（後端不強制，因為分車方式可能更複雜）。 */
function PanelSubset({ all, selected, excludeUsedBy, onChange }: {
  all: string[]
  selected: string[]
  excludeUsedBy: string[]
  onChange: (ids: string[]) => void
}) {
  const [showAll, setShowAll] = useState(false)
  const [n, setN] = useState('')
  const taken = new Set(excludeUsedBy)
  const visible = showAll ? all : all.slice(0, 40)

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-end gap-2">
        <span className="text-sm tabular-nums">已選 {count(selected.length)} / {count(all.length)}</span>
        <div className="flex items-end gap-1">
          <TextInput inputMode="numeric" value={n} placeholder="前 N 片" className="w-[100px]"
            onChange={(e) => setN(e.target.value.replace(/\D/g, ''))} />
          <Button size="sm" onClick={() => {
            const k = Number(n)
            if (!k) return
            onChange(all.filter((p) => !taken.has(p)).slice(0, k))
          }}>取前 N 片</Button>
        </div>
        <Button size="sm" variant="ghost"
          onClick={() => onChange(all.filter((p) => !taken.has(p)))}>選剩下全部</Button>
        <Button size="sm" variant="ghost" onClick={() => onChange([])}>清除</Button>
      </div>
      <div className="max-h-40 overflow-y-auto rounded border border-ink-200 p-2">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-x-3 gap-y-0.5">
          {visible.map((p) => {
            const usedElsewhere = taken.has(p)
            return (
              <label key={p} className={`flex items-center gap-1.5 rounded px-1 py-0.5 text-xs ${
                usedElsewhere ? 'text-ink-300' : 'cursor-pointer hover:bg-ink-50'}`}>
                <input type="checkbox" className="h-3.5 w-3.5" disabled={usedElsewhere}
                  checked={selected.includes(p)}
                  onChange={(e) => onChange(e.target.checked
                    ? all.filter((x) => selected.includes(x) || x === p)
                    : selected.filter((x) => x !== p))} />
                <span className="truncate font-mono">{p}</span>
                {usedElsewhere && <span title="已被其他車選走">已用</span>}
              </label>
            )
          })}
        </div>
        {!showAll && all.length > 40 && (
          <Button size="sm" variant="ghost" className="mt-1" onClick={() => setShowAll(true)}>
            顯示全部 {count(all.length)} 片
          </Button>
        )}
      </div>
    </div>
  )
}

// ── 補秤（mockup §10 步 17）────────────────────────────────────────────────

function SupplementForm({ workOrderId, station, lines, config, onPosted }: {
  workOrderId: string
  station: string
  lines: Receipt[]
  config: NonNullable<ReturnType<typeof useConfig>['config']>
  onPosted: () => void
}) {
  const dict = useDict()
  const [target, setTarget] = useState(lines[0]?.receipt_line_id ?? '')
  const [weigh, setWeigh] = useState<WeighValue>(() => emptyWeighValue())

  const line = lines.find((l) => l.receipt_line_id === target)
  const mp = line
    ? config.measurementPoints.find(
        (m) => m.stationId === station && m.kind === 'receipt' && m.materialCode === line.material_code,
      ) ?? null
    : null

  const post = useMutation<void, unknown>(async (_a, key) =>
    api.post('/api/receipts', {
      workOrderId,
      supplements: [{
        receiptLineId: target,
        measurementPointId: mp!.measurementPointId,
        weigh: toWeighPayload(mp!, weigh, []),
      }],
    }, { idempotencyKey: key }))

  return (
    <Card title="補秤" subtitle="為既有的繳庫行補上重量。事件只新增——原本那筆無重量的紀錄不會消失" tone="warn">
      <Alert tone="info" title="這是「量測缺口只是設定」的補救路徑">
        量測點啟用之後，用這裡把先前沒秤到的批補上重量。補完之後站台結算就會從
        「無法結算」變成正常——資料模型從頭到尾沒有改，改的只是設定。
      </Alert>

      <div className="mt-3">
        <Field label="要補哪一筆" required>
          <Select value={target} onChange={(e) => { setTarget(e.target.value); setWeigh(emptyWeighValue()) }}>
            {lines.map((l) => (
              <option key={l.receipt_line_id} value={l.receipt_line_id}>
                {l.receipt_line_id} · {dict.material(l.material_code)} · {l.panel_count} 片
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {line && !mp && (
        <div className="mt-3">
          <Alert tone="warn">本站沒有針對 {dict.material(line.material_code)} 的繳庫量測點。</Alert>
        </div>
      )}

      {mp && !mp.enabled && (
        <div className="mt-3">
          <Alert tone="warn" title="量測點還是停用的">
            <Mono>{mp.measurementPointId}</Mono> 目前停用，所以還不能補秤。
            請主管先到<Link to="/settings" className="mx-1 underline">作業設定</Link>啟用它。
          </Alert>
        </div>
      )}

      {mp?.enabled && (
        <>
          <div className="mt-3">
            {/* 補秤通常是整批（那批已經在車上了），逐片的話要重新掃每一片 */}
            <WeighEntry
              mp={mp} config={config} materialCode={line!.material_code}
              panels={[]} value={weigh} onChange={setWeigh}
            />
          </div>
          {post.error && <div className="mt-3"><Alert tone="danger">{post.error.message}</Alert></div>}
          <div className="mt-4">
            <Button variant="primary" size="lg"
              disabled={mp.granularity === 'panel' || !weighComplete(mp, weigh, []) || post.pending}
              onClick={async () => {
                const r = await post.run()
                if (r !== null) { setWeigh(emptyWeighValue()); onPosted() }
              }}>
              {post.pending ? '補秤中…' : '補上重量'}
            </Button>
            {mp.granularity === 'panel' && (
              <p className="mt-2 text-xs text-amber-700">
                這個量測點是逐片粒度，補秤要逐片重新掃。目前的畫面只支援整批補秤——
                逐片補秤請走一般繳庫流程。
              </p>
            )}
          </div>
        </>
      )}
    </Card>
  )
}
